import { doc, getDoc, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { LegacyCurrentSalarySnapshot } from '../Models/collaboratorSalary.types';

/**
 * Service to read salary data from the new subcollection structure (post-migration)
 *
 * Structure:
 * collaborators_private/{id}/salary/current
 * collaborators_private/{id}/salary/current/history/{historyId}
 */

export interface SalaryConfigData {
  allowRemove: boolean;
  showHistory: boolean;
  createdAt?: any;
  createdBy?: string;
  updatedAt?: any;
  updatedBy?: string;
}

export interface SalaryHistoryEntry {
  id: string;
  amount: number;
  extras: number;
  salaryType: 'CLT' | 'PJ' | 'Estagiário' | 'Sócio';
  changeDate: string;
  changeReason: string;
  changeObservations?: string;
  recordedAt: any;
  recordedBy: string;
}

export interface CollaboratorSalaryData {
  config: SalaryConfigData | null;
  history: SalaryHistoryEntry[];
  currentEntry: SalaryHistoryEntry | null;
  upcomingEntry: SalaryHistoryEntry | null;
}

const determineTimeline = (
  history: SalaryHistoryEntry[],
  legacyCurrent?: LegacyCurrentSalarySnapshot | null
): { currentEntry: SalaryHistoryEntry | null; upcomingEntry: SalaryHistoryEntry | null } => {
  const sortedHistory = [...history].sort((a, b) => {
    const aDate = new Date(a.changeDate);
    const bDate = new Date(b.changeDate);
    return aDate.getTime() - bDate.getTime();
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const pastOrPresent = sortedHistory.filter((entry) => {
    const entryDate = new Date(entry.changeDate);
    entryDate.setHours(0, 0, 0, 0);
    return entryDate.getTime() <= today.getTime();
  });

  const futureEntries = sortedHistory.filter((entry) => {
    const entryDate = new Date(entry.changeDate);
    entryDate.setHours(0, 0, 0, 0);
    return entryDate.getTime() > today.getTime();
  });

  let currentEntry: SalaryHistoryEntry | null = pastOrPresent.length
    ? pastOrPresent[pastOrPresent.length - 1]
    : null;

  const upcomingEntry: SalaryHistoryEntry | null = futureEntries.length
    ? futureEntries[0]
    : null;

  if (!currentEntry && legacyCurrent) {
    currentEntry = {
      id: 'legacy-current',
      amount: legacyCurrent.amount,
      extras: legacyCurrent.extras,
      salaryType: legacyCurrent.salaryType,
      changeDate: legacyCurrent.effectiveDate,
      changeReason: 'Salário vigente (legado)',
      changeObservations: undefined,
      recordedAt: new Date(),
      recordedBy: 'legacy',
    };
  }

  return {
    currentEntry,
    upcomingEntry,
  };
};

/**
 * Fetch salary data for a single collaborator from the subcollection
 */
export async function fetchCollaboratorSalary(
  collaboratorId: string
): Promise<CollaboratorSalaryData> {
  try {
    const salaryRef = doc(db, 'collaborators_private', collaboratorId, 'salary', 'current');
    const salaryDoc = await getDoc(salaryRef);

    let config: SalaryConfigData | null = null;
    let legacyCurrent: LegacyCurrentSalarySnapshot | null = null;

    if (salaryDoc.exists()) {
      const rawData = salaryDoc.data() as Record<string, any>;

      const hasConfigFields =
        rawData.allowRemove !== undefined ||
        rawData.showHistory !== undefined ||
        rawData.createdAt !== undefined ||
        rawData.updatedAt !== undefined;

      if (hasConfigFields) {
        config = {
          allowRemove: rawData.allowRemove ?? true,
          showHistory: rawData.showHistory ?? true,
          createdAt: rawData.createdAt,
          createdBy: rawData.createdBy,
          updatedAt: rawData.updatedAt,
          updatedBy: rawData.updatedBy,
        };
      }

      if (typeof rawData.currentAmount === 'number') {
        legacyCurrent = {
          amount: rawData.currentAmount,
          extras: rawData.currentExtras ?? 0,
          salaryType: rawData.currentSalaryType ?? 'CLT',
          effectiveDate: rawData.effectiveDate ?? new Date().toISOString().slice(0, 10),
        };
      }
    }

    const historyRef = collection(salaryRef, 'history');
    const historyQuery = query(historyRef, orderBy('changeDate', 'desc'));
    const historySnapshot = await getDocs(historyQuery);

    const history = historySnapshot.docs.map((historyDoc) => ({
      id: historyDoc.id,
      ...historyDoc.data(),
    })) as SalaryHistoryEntry[];

    const { currentEntry, upcomingEntry } = determineTimeline(history, legacyCurrent);

    return {
      config,
      history,
      currentEntry,
      upcomingEntry,
    };
  } catch (error) {
    console.error(`Error fetching salary for collaborator ${collaboratorId}:`, error);
    return {
      config: null,
      history: [],
      currentEntry: null,
      upcomingEntry: null,
    };
  }
}

/**
 * Fetch salary data for multiple collaborators
 */
export async function fetchCollaboratorsSalary(
  collaboratorIds: string[]
): Promise<Record<string, CollaboratorSalaryData>> {
  const salaryDataMap: Record<string, CollaboratorSalaryData> = {};

  // Fetch all salaries in parallel
  const promises = collaboratorIds.map(async (id) => {
    const salaryData = await fetchCollaboratorSalary(id);
    salaryDataMap[id] = salaryData;
  });

  await Promise.all(promises);

  return salaryDataMap;
}

/**
 * Convert salary subcollection data to old format for backward compatibility
 * This allows existing components to work with the new structure
 *
 * IMPORTANTE: NÃO retorna salaryHistorySalaries porque o histórico é
 * carregado separadamente pelo hook useCollaboratorSalary.
 * O campo salaryHistorySalaries deve conter APENAS novos registros não salvos.
 */
export function convertToLegacyFormat(salaryData: CollaboratorSalaryData): {
  newSalaryEntryAmount?: number;
  newSalaryEntryExtras?: number;
  newSalaryEntrySalaryType?: string;
  newSalaryEntryDate?: string;
  salaryHistoryAllowRemove?: boolean;
  salaryHistoryShowHistory?: boolean;
  salaryHistorySalaries?: Array<{
    id: string;
    date: string;
    amount: number;
    extras?: number;
    salaryType: string;
    reason?: string;
    observations?: string;
    recordedAt?: any;
    recordedBy?: string;
  }>;
} {
  const entry = salaryData.currentEntry ?? salaryData.upcomingEntry;

  const historyMapped = salaryData.history.map((historyEntry) => ({
    id: historyEntry.id,
    date: historyEntry.changeDate,
    amount: historyEntry.amount,
    extras: historyEntry.extras ?? 0,
    salaryType: historyEntry.salaryType,
    reason: historyEntry.changeReason,
    observations: historyEntry.changeObservations,
    recordedAt: historyEntry.recordedAt,
    recordedBy: historyEntry.recordedBy,
  }));

  const baseConfig = salaryData.config
    ? {
        salaryHistoryAllowRemove: salaryData.config.allowRemove,
        salaryHistoryShowHistory: salaryData.config.showHistory,
      }
    : {};

  if (!entry) {
    return {
      ...baseConfig,
      salaryHistorySalaries: historyMapped,
    };
  }

  return {
    ...baseConfig,
    newSalaryEntryAmount: entry.amount,
    newSalaryEntryExtras: entry.extras ?? 0,
    newSalaryEntrySalaryType: entry.salaryType,
    newSalaryEntryDate: entry.changeDate,
    salaryHistorySalaries: historyMapped,
  };
}
